import React, { useContext, useState, useCallback, useMemo } from "react";
import "./classification-tab-content.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { CaseContext } from "../../../../../store";
import { AITechnique } from "../../../types/AI-techniques";
import { MachineLearningType } from "../../../types/machine-learning-types";
import { AIAlgorithm } from "../../../types/AI-algorithm";
import SelectionBox from "../../create/selection-box/selection-box.component";
import { icons } from "../../../../../statics/icons";
import { Skeleton } from "@material-ui/lab";
import TabContentTitleComponent from "../tab-content-title/tab-content-title.component";
import AppCheckboxComponent from "../../../../../components/form/app-checkbox";

// Constants
const SKELETON_HEIGHTS = {
  CHECKBOX: 30,
  SELECTION_BOX: 80,
  DESCRIPTION: 200,
} as const;

const ClassificationTabContentComponent: React.FC = () => {
  const { t } = useTranslation();

  const {
    algorithms,
    machineLearningTypes,
    techniques,
    classificationLoading,
    setCaseDetailsForm,
    caseDetailsForm,
  } = useContext(CaseContext);
  const [algorithmHover, setAlgorithmHover] = useState<number>(-1);

  /**
   * Safe arrays - ensure they're always arrays even if undefined
   */
  const safeTechniques = useMemo(() => {
    return Array.isArray(techniques) ? techniques : [];
  }, [techniques]);

  const safeAlgorithms = useMemo(() => {
    return Array.isArray(algorithms) ? algorithms : [];
  }, [algorithms]);

  const safeMachineLearningTypes = useMemo(() => {
    return Array.isArray(machineLearningTypes) ? machineLearningTypes : [];
  }, [machineLearningTypes]);

  /**
   * Generic function to toggle checkbox selection (techniques or machine learning)
   */
  const handleCheckboxToggle = useCallback(
    (
      id: number,
      fieldName: "techniques" | "machineLearning",
      isChecked: boolean,
    ) => {
      const idsField = `${fieldName}Ids` as
        | "techniquesIds"
        | "machineLearningIds";
      const currentIds = caseDetailsForm[idsField] || [];
      const currentItems = caseDetailsForm[fieldName] || [];

      if (isChecked) {
        // Add item
        const sourceArray =
          fieldName === "techniques"
            ? safeTechniques
            : safeMachineLearningTypes;
        const item = sourceArray.find((item: any) => item?.id === id);

        if (item) {
          setCaseDetailsForm({
            ...caseDetailsForm,
            [idsField]: [...currentIds, id],
            [fieldName]: [...currentItems, item],
          });
        }
      } else {
        // Remove item
        setCaseDetailsForm({
          ...caseDetailsForm,
          [idsField]: currentIds.filter((itemId: number) => itemId !== id),
          [fieldName]: currentItems.filter((item: any) => item?.id !== id),
        });
      }
    },
    [
      caseDetailsForm,
      setCaseDetailsForm,
      safeTechniques,
      safeMachineLearningTypes,
    ],
  );

  /**
   * Handle checkbox change event
   */
  const handleCheckbox = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fieldName =
        e.target.name === "techniquesIds" ? "techniques" : "machineLearning";
      const id = Number(e.target.value);
      const isChecked = e.target.checked;

      handleCheckboxToggle(id, fieldName, isChecked);
    },
    [handleCheckboxToggle],
  );

  /**
   * Toggle algorithm selection (multi-select)
   */
  const handleAlgorithmToggle = useCallback(
    (id: number | string) => {
      const algorithmId = Number(id);
      const currentIds = caseDetailsForm.algorithmsIds || [];
      const currentAlgorithms = caseDetailsForm.algorithms || [];
      const isSelected = currentIds.includes(algorithmId);

      if (isSelected) {
        // Remove algorithm
        setCaseDetailsForm({
          ...caseDetailsForm,
          algorithmsIds: currentIds.filter(
            (itemId: number) => itemId !== algorithmId,
          ),
          algorithms: currentAlgorithms.filter(
            (alg: any) => alg?.id !== algorithmId,
          ),
        });
      } else {
        // Add algorithm
        const algorithm = safeAlgorithms.find(
          (alg: AIAlgorithm) => alg?.id === algorithmId,
        );
        if (algorithm) {
          setCaseDetailsForm({
            ...caseDetailsForm,
            algorithmsIds: [...currentIds, algorithmId],
            algorithms: [...currentAlgorithms, algorithm],
          });
        }
      }
    },
    [caseDetailsForm, setCaseDetailsForm, safeAlgorithms],
  );

  /**
   * Check if an algorithm is selected
   */
  const isAlgorithmSelected = useCallback(
    (algorithmId: number): boolean => {
      return (caseDetailsForm.algorithmsIds || []).includes(algorithmId);
    },
    [caseDetailsForm.algorithmsIds],
  );

  /**
   * Check if a technique is selected
   */
  const isTechniqueSelected = useCallback(
    (techniqueId: number): boolean => {
      return (caseDetailsForm.techniquesIds || []).includes(techniqueId);
    },
    [caseDetailsForm.techniquesIds],
  );

  /**
   * Check if a machine learning type is selected
   */
  const isMachineLearningSelected = useCallback(
    (mlId: number): boolean => {
      return (caseDetailsForm.machineLearningIds || []).includes(mlId);
    },
    [caseDetailsForm.machineLearningIds],
  );

  /**
   * Get icon for algorithm
   */
  const getAlgorithmIcon = useCallback(
    (iconKey?: string, isActive: boolean = false) => {
      if (!iconKey) {
        return isActive ? icons.categoryWhite : icons.categoryPrimary;
      }
      const iconKeyName = isActive ? `${iconKey}_WHITE` : iconKey;
      return (
        icons.PAGES.SETTINGS.ICON_OPTIONS[iconKeyName] || icons.categoryPrimary
      );
    },
    [],
  );

  /**
   * Render technique checkbox
   */
  const renderTechniqueCheckbox = useCallback(
    (item: AITechnique, index: number) => {
      // Safety check
      if (!item || !item.id) return null;

      const isSelected = isTechniqueSelected(item.id);

      return (
        <Box key={item.id}>
          {classificationLoading ? (
            <Skeleton
              variant="text"
              height={SKELETON_HEIGHTS.CHECKBOX}
              width="100%"
            />
          ) : (
            <AppCheckboxComponent
              checked={isSelected}
              value={item.id}
              onChange={handleCheckbox}
              name="techniquesIds"
              label={item.name || ""}
              showInfoIcon
              infoIcon={icons.ROUND_INFO}
              size="medium"
            />
            // <FormControlLabel
            //   className="case__classification__checkbox"
            //   control={
            //     <Checkbox
            //       checked={isSelected}
            //       value={item.id}
            //       onChange={handleCheckbox}
            //       className="checkbox__root"
            //       checkedIcon={
            //         <span className={clsx("checkbox__icon", "checked")} />
            //       }
            //       icon={<span className="checkbox__icon" />}
            //       name="techniquesIds"
            //     />
            //   }
            //   label={
            //     <Box className="checkbox__label">
            //       <Typography variant="body1" color="textPrimary">
            //         {item.name || ""}
            //       </Typography>
            //       <img src={icons.ROUND_INFO} alt="info" />
            //     </Box>
            //   }
            // />
          )}
        </Box>
      );
    },
    [classificationLoading, isTechniqueSelected, handleCheckbox],
  );

  /**
   * Render machine learning checkbox
   */
  const renderMachineLearningCheckbox = useCallback(
    (item: MachineLearningType, index: number) => {
      // Safety check
      if (!item || !item.id) return null;

      const isSelected = isMachineLearningSelected(item.id);

      return (
        <Box key={item.id}>
          {classificationLoading ? (
            <Skeleton
              variant="text"
              height={SKELETON_HEIGHTS.CHECKBOX}
              width="100%"
            />
          ) : (
            <AppCheckboxComponent
              checked={isSelected}
              value={item.id}
              onChange={handleCheckbox}
              name="machineLearningIds"
              label={item.name || ""}
              showInfoIcon
              size="medium"
              infoIcon={icons.ROUND_INFO}
            />
          )}
        </Box>
      );
    },
    [classificationLoading, isMachineLearningSelected, handleCheckbox],
  );

  /**
   * Render algorithm selection box
   */
  const renderAlgorithmBox = useCallback(
    (item: AIAlgorithm, index: number) => {
      // Safety check
      if (!item || !item.id) return null;

      const isSelected = isAlgorithmSelected(item.id);
      const isHovered = algorithmHover === index;
      const isActive = isSelected || isHovered;

      return (
        <Box
          key={item.id}
          onMouseEnter={() => setAlgorithmHover(index)}
          onMouseLeave={() => setAlgorithmHover(-1)}
        >
          {classificationLoading ? (
            <Skeleton
              variant="text"
              height={SKELETON_HEIGHTS.SELECTION_BOX}
              width="100%"
            />
          ) : (
            <SelectionBox
              title={item.name || ""}
              subTitle={item.description || ""}
              icon={getAlgorithmIcon(item.iconKey, false)}
              activeIcon={getAlgorithmIcon(item.iconKey, true)}
              active={isActive}
              itemKey={item.id}
              onBoxClick={handleAlgorithmToggle}
            />
          )}
        </Box>
      );
    },
    [
      classificationLoading,
      algorithmHover,
      isAlgorithmSelected,
      getAlgorithmIcon,
      handleAlgorithmToggle,
    ],
  );

  return (
    <Box className="classification-tab">
      <TabContentTitleComponent
        title={t("USECASE_DETAILS.CLASSIFICATION_OF_THE_USE_CASE")}
        subTitle={t("USECASE_DETAILS.CLASSIFICATION_OF_THE_USE_CASE_SUB_TITLE")}
        loading={classificationLoading}
      />
      <Box className="global__box__content p-0 mb-3">
        <Box className="global__box__content__header">
          {classificationLoading ? (
            <Skeleton variant="text" height={20} width={350} />
          ) : (
            <Typography
              className="text-capitalize"
              variant="h3"
              color="textPrimary"
            >
              {t("USECASE_DETAILS.SELECT_THE_AI_ALGORITHMS")}
            </Typography>
          )}
        </Box>
        <Box className="global__box__content__body my-2">
          <Box className="items">
            {classificationLoading ? (
              // Show skeleton loaders when loading
              Array.from({ length: 10 }).map((_, index) => (
                <Skeleton
                  key={`algorithm-skeleton-${index}`}
                  variant="text"
                  height={SKELETON_HEIGHTS.SELECTION_BOX}
                  width="100%"
                />
              ))
            ) : safeAlgorithms.length > 0 ? (
              safeAlgorithms.map((item: AIAlgorithm, index: number) =>
                renderAlgorithmBox(item, index),
              )
            ) : (
              // Show empty state if no data and not loading
              <Typography variant="body1" color="textSecondary">
                {t("No algorithms available")}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          className="global__box__content__header"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          {classificationLoading ? (
            <Skeleton variant="text" height={20} width={350} />
          ) : (
            <Typography
              className="text-capitalize"
              variant="h3"
              color="textPrimary"
            >
              {t("USECASE_DETAILS.SELECT_THE_MACHINE_LEARNING_TYPES")}
            </Typography>
          )}
        </Box>
        <Box className="global__box__content__body my-2">
          <Box className="machine-types">
            {classificationLoading ? (
              // Show skeleton loaders when loading
              Array.from({ length: 8 }).map((_, index) => (
                <Skeleton
                  key={`ml-skeleton-${index}`}
                  variant="text"
                  height={SKELETON_HEIGHTS.CHECKBOX}
                  width="100%"
                  style={{ marginBottom: "8px" }}
                />
              ))
            ) : safeMachineLearningTypes.length > 0 ? (
              safeMachineLearningTypes.map(
                (item: MachineLearningType, index: number) =>
                  renderMachineLearningCheckbox(item, index),
              )
            ) : (
              // Show empty state if no data and not loading
              <Typography variant="body1" color="textSecondary">
                {t("No machine learning types available")}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          className="global__box__content__header"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          {classificationLoading ? (
            <Skeleton variant="text" height={20} width={350} />
          ) : (
            <Typography
              className="text-capitalize"
              variant="h3"
              color="textPrimary"
            >
              {t("USECASE_DETAILS.SELECT_THE_AI_TECHNIQUES_AND_APPROACHES")}
            </Typography>
          )}
        </Box>
        <Box className="global__box__content__body my-2">
          <Box className="techniques">
            {classificationLoading ? (
              // Show skeleton loaders when loading
              Array.from({ length: 8 }).map((_, index) => (
                <Skeleton
                  key={`technique-skeleton-${index}`}
                  variant="text"
                  height={SKELETON_HEIGHTS.CHECKBOX}
                  width="100%"
                  style={{ marginBottom: "8px" }}
                />
              ))
            ) : safeTechniques.length > 0 ? (
              safeTechniques.map((item: AITechnique, index: number) =>
                renderTechniqueCheckbox(item, index),
              )
            ) : (
              // Show empty state if no data and not loading
              <Typography variant="body1" color="textSecondary">
                {t("No techniques available")}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ClassificationTabContentComponent;
